import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { api, fetcher } from '@plugins/axios/api';

import { Button } from '@components/button/Button';
import { TableInputField } from '@components/field/TableInputField';
import { BasicTable } from '@components/table/BasicTable';
import { PageTitle } from '@components/title/PageTitle';

import { PageInfo, Paginated } from '@mocks/api';
import { ITEM_LIST } from '@mocks/projects';

import ItemSampleImg from '@assets/imgs/items/item_sample.png';

export const FUpsertOrderPage = () => {
  const [orderDetail, setOrderDetail] = useState<{
    [name: string]: any;
  }>({});
  const [isUpdate, setIsUpdate] = useState(false);
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, refetch, isFetching } = useQuery<any>({
    queryKey: [`/funeral/orders/${orderId}`],
    queryFn: fetcher,
    staleTime: 10 * 1000,
    enabled: orderId != undefined,
  });

  const tableInputCell = useCallback(
    (info: any) => {
      const itemId = info.getValue();
      return (
        <div className='flex flex-row'>
          <TableInputField
            className='w-[60px] border-none text-right'
            id={itemId}
            type='number'
            placeholder='0'
            initData={orderDetail[itemId] ?? 0}
            setData={setOrderDetail}
          />
          <p className='self-center pr-1 pt-[2px] text-gray-500'>박스</p>
        </div>
      );
    },
    [isUpdate],
  );

  const orderDetailList = useMemo(() => {
    let detaiList: number[] = [];
    Object.entries(orderDetail).forEach(([k, v]) => {
      if (v != undefined && v != 0 && v != '') {
        detaiList = detaiList.concat(Array(Number(v)).fill(k));
      }
    });
    return detaiList;
  }, [orderDetail]);

  const onSubmit = useCallback(() => {
    if (orderDetailList.length === 0) {
      alert('주문 수량을 입력해주세요.');
      return;
    }

    api
      .post('/funeral/orders', {
        order_items: orderDetailList,
      })
      .then((res) => {
        const { data } = res;

        if ('item_id' in data && data['item_id'] != undefined) {
          alert('주문 요청에 성공했습니다');
          navigate('/funeral/orders/' + data['item_id']);
        } else {
          alert('주문 요청 실패');
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [orderDetailList, navigate]);

  const onModifySubmit = useCallback(() => {
    if (orderDetailList.length === 0) {
      alert('주문 수량을 입력해주세요.');
      return;
    }

    api
      .put(`/funeral/orders/${orderId}`, {
        order_items: orderDetailList,
      })
      .then((res) => {
        const { data } = res;

        if ('item_id' in data && data['item_id'] != undefined) {
          alert('주문 수정 요청에 성공했습니다');
          navigate('/funeral/orders/' + data['item_id']);
        } else {
          alert('주문 수정 요청 실패');
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [orderDetailList, orderId, navigate]);

  useEffect(() => {
    if (!isFetching && data != undefined) {
      console.log(data);

      if ('item' in data && 'order_items' in data['item']) {
        const orderItems: Array<number> = data['item']['order_items'] ?? [];
        const tmpOrderDetail: { [name: string]: number } = {};
        orderItems.forEach((itemId: number) => {
          if (itemId in tmpOrderDetail) {
            tmpOrderDetail[itemId] += 1;
          } else {
            tmpOrderDetail[itemId] = 1;
          }
        });
        setOrderDetail((prev) => ({ ...prev, ...tmpOrderDetail }));
        setIsUpdate(!isUpdate);
      }
    }
  }, [isFetching, data]);

  useEffect(() => {
    if (orderId != undefined) {
      refetch();
    }
    if (location.pathname.includes('/new-order')) {
      setOrderDetail({});
      setIsUpdate(!isUpdate);
    }
  }, [orderId, location]);

  return (
    <div className='container'>
      <PageTitle className='mt-4' titleHidden />
      <h1 className='text-3xl font-bold leading-[170%] tracking-tight'>
        다회용기 주문
      </h1>
      {data != undefined && (
        <section className='mt-3 flex flex-row flex-wrap rounded-lg border border-gray-200 p-4'>
          <div className='mb-3 flex w-1/2 flex-row'>
            <p className='text-lg font-bold'>주문번호</p>
            <p className='flex-1 text-center text-lg'>{data['item']['idx']}</p>
          </div>
          <div className='mb-3 flex w-1/2 flex-row'>
            <p className='text-lg font-bold'>주문상태</p>
            <p className='flex-1 text-center text-lg'>
              {data['item']['order_state'] == 0 ? '미접수' : ''}
            </p>
          </div>
          <div className='flex w-full flex-row'>
            <p className='text-lg font-bold'>주문일자</p>
            <p className='flex-1 text-center text-lg'>
              {format(
                new Date(data['item']['insert_at']),
                'yyyy-MM-dd HH시 mm분',
              )}
            </p>
          </div>
        </section>
      )}
      <img className='mt-3 h-full w-full object-cover' src={ItemSampleImg} />
      <section className='mt-6'>
        <BasicTable
          captionPosition='top'
          isLoading={isFetching}
          columns={[
            {
              header: '품번',
              accessorKey: 'item_id',
              minSize: 30,
              maxSize: 30,
            },
            {
              header: '품목',
              accessorKey: 'short_name',
              minSize: 140,
              maxSize: 140,
            },
            {
              header: '입수량\n(박스당)',
              accessorKey: 'unit_per',
              minSize: 100,
              maxSize: 100,
            },
            {
              header: '주문수량\n(박스)',
              accessorKey: 'count',
              minSize: 100,
              maxSize: 100,
              cell: tableInputCell,
            },
          ]}
          data={ITEM_LIST}
          className='flex h-fit w-full flex-col gap-12'
        />
        <div className='mt-3'>
          <p className='text-xl font-bold text-gray-900'>
            총 주문 수량 :
            <span className='px-2 pl-4'>{orderDetailList.length}</span>
            <span className='text-gray-500'>박스</span>
          </p>
        </div>
      </section>
      <section className='mt-6 flex flex-row justify-end'>
        {orderId == undefined && (
          <Button
            disabled={orderDetailList.length === 0}
            className='w-40'
            onClick={onSubmit}
          >
            주문하기
          </Button>
        )}
        {orderId != undefined && (
          <Button
            disabled={orderDetailList.length === 0}
            className='w-40'
            onClick={onModifySubmit}
          >
            수정완료
          </Button>
        )}
      </section>
    </div>
  );
};
